import type { DrawingDocument, Provenance } from '../model';
import {
  aromaticAtomIds,
  aromaticBondIds,
  aromaticityResult,
  atomStereoValues,
  CALCULATED_PROVENANCE,
  cipResult,
  orderedStereoLigands,
  partialChargeResult
} from './documentSelectors.ts';
import { changeStructure, createId } from './drawingDocumentCore.ts';

// Trivial wrappers duplicated from drawingDocumentProjections.ts rather than imported: this
// module records externally-computed chemistry, that one derives rendering projections, and
// neither needs to depend on the other for two three-line reads.
function aromaticAtomValues(document: DrawingDocument): Readonly<Record<string, { readonly aromatic: true }>> {
  return Object.fromEntries([...aromaticAtomIds(document)].map((id) => [id, { aromatic: true as const }]));
}

function aromaticBondValues(document: DrawingDocument): Readonly<Record<string, { readonly aromatic: true }>> {
  return Object.fromEntries([...aromaticBondIds(document)].map((id) => [id, { aromatic: true as const }]));
}

export function setPerceivedAtomStereo(
  document: DrawingDocument,
  values: Readonly<
    Record<
      string,
      Readonly<{
        parity: 'clockwise' | 'counterclockwise';
        descriptor?: 'R' | 'S';
      }>
    >
  >,
  // #780: each double bond's E or Z, written with the R/S labels (derived, never authored).
  bondDescriptors: Readonly<Record<string, 'E' | 'Z'>> = {}
): DrawingDocument {
  const bondLabels = Object.fromEntries(
    Object.entries(bondDescriptors)
      .filter(([edgeId]) => document.structure.bonds[edgeId] !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
  );
  const normalized = Object.fromEntries(
    Object.entries(values).filter(
      ([nodeId]) => document.structure.atoms[nodeId] !== undefined && orderedStereoLigands(document, nodeId)
    )
  );
  const currentBondLabels = Object.fromEntries(
    Object.entries(cipResult(document)?.bondDescriptors ?? {}).sort(([a], [b]) => a.localeCompare(b))
  );
  if (
    JSON.stringify(normalized) === JSON.stringify(atomStereoValues(document)) &&
    JSON.stringify(bondLabels) === JSON.stringify(currentBondLabels)
  ) {
    return document;
  }
  const tetrahedral = Object.fromEntries(
    Object.entries(normalized).map(([atomId, value]) => [
      atomId,
      { ligands: orderedStereoLigands(document, atomId)!, winding: value.parity }
    ])
  );
  const revision = document.structure.revision + 1;
  const next = changeStructure(document, {
    stereochemistry: { ...document.structure.stereochemistry, tetrahedral }
  });
  const retained = next.derivedPropertySets.filter((result) => result.kind !== 'cip');
  const atomDescriptors = Object.fromEntries(
    Object.entries(normalized).flatMap(([id, value]) => (value.descriptor ? [[id, value.descriptor]] : []))
  );
  return {
    ...next,
    derivedPropertySets:
      Object.keys(atomDescriptors).length || Object.keys(bondLabels).length
        ? [
            ...retained,
            {
              id: cipResult(document)?.id ?? createId('cip'),
              kind: 'cip',
              structureId: next.structure.id,
              structureRevision: revision,
              atomDescriptors,
              bondDescriptors: bondLabels,
              provenance: CALCULATED_PROVENANCE
            }
          ]
        : retained
  };
}

export function setPerceivedAromaticity(
  document: DrawingDocument,
  atomIds: readonly string[],
  bondIds: readonly string[]
): DrawingDocument {
  const graphNodeIds = new Set(document.structure.graph.nodes.map((node) => node.id));
  const graphEdgeIds = new Set(document.structure.graph.edges.map((edge) => edge.id));
  const normalizedAtomIds = [...new Set(atomIds)]
    .filter((id) => graphNodeIds.has(id))
    .sort((a, b) => a.localeCompare(b));
  const normalizedBondIds = [...new Set(bondIds)]
    .filter((id) => graphEdgeIds.has(id))
    .sort((a, b) => a.localeCompare(b));
  const currentAtomIds = Object.keys(aromaticAtomValues(document)).sort((a, b) => a.localeCompare(b));
  const currentBondIds = Object.keys(aromaticBondValues(document)).sort((a, b) => a.localeCompare(b));
  if (
    normalizedAtomIds.join('\0') === currentAtomIds.join('\0') &&
    normalizedBondIds.join('\0') === currentBondIds.join('\0')
  ) {
    return document;
  }
  const retained = document.derivedPropertySets.filter((result) => result.kind !== 'aromaticity');
  return {
    ...document,
    derivedPropertySets:
      normalizedAtomIds.length || normalizedBondIds.length
        ? [
            ...retained,
            {
              id: aromaticityResult(document)?.id ?? createId('aromaticity'),
              kind: 'aromaticity',
              structureId: document.structure.id,
              structureRevision: document.structure.revision,
              atomIds: normalizedAtomIds,
              bondIds: normalizedBondIds,
              provenance: CALCULATED_PROVENANCE
            }
          ]
        : retained
  };
}

export function setPartialCharges(
  document: DrawingDocument,
  charges: Readonly<Record<string, number>>,
  provenance: Provenance
): DrawingDocument {
  const graphNodeIds = new Set(document.structure.graph.nodes.map((node) => node.id));
  const filteredCharges = Object.fromEntries(Object.entries(charges).filter(([nodeId]) => graphNodeIds.has(nodeId)));
  const retained = document.derivedPropertySets.filter((result) => result.kind !== 'partial-charge');
  if (Object.keys(filteredCharges).length === 0) return { ...document, derivedPropertySets: retained };
  return {
    ...document,
    derivedPropertySets: [
      ...retained,
      {
        id: partialChargeResult(document)?.id ?? createId('partial-charge'),
        kind: 'partial-charge',
        structureId: document.structure.id,
        structureRevision: document.structure.revision,
        units: 'elementary-charge',
        charges: filteredCharges,
        provenance
      }
    ]
  };
}
